import { useState, useRef } from 'react';
import { EnquiryFormProps } from '../types/EnquiryFormProps';
import { Button } from './Button';
import { Close } from './icons/Close';
import { Control } from './forms/Control';
import { Input } from './forms/Input';
import { TextArea } from './forms/TextArea';
import { FormDataType } from '../types/FormDataType';
import { fetcher } from '../utils/fetcher';
import { Select } from './forms/Select';
import ReCAPTCHA from "react-google-recaptcha";
import { SelectOption } from '../types/SelectProps';
import { CheckCircle } from './icons/CheckCircle';
const relationships = [
    {
        "value": "FR",
        "label": "Father"
    },
    {
        "value": "MR",
        "label": "Mother"
    },
    {
        "value": "STFR",
        "label": "Stepfather"
    },
    {
        "value": "STMR",
        "label": "Stepmother"
    },
    {
        "value": "UE",
        "label": "Uncle"
    },
    {
        "value": "AT",
        "label": "Aunt"
    },
    {
        "value": "BR",
        "label": "Brother"
    },
    {
        "value": "SR",
        "label": "Sister"
    },
    {
        "value": "CN",
        "label": "Cousin"
    },
    {
        "value": "GRAFR",
        "label": "Grandfather"
    },
    {
        "value": "GRAMR",
        "label": "Grandmother"
    },
    {
        "value": "GD",
        "label": "Guardian"
    },
    {
        "value": "FOPT",
        "label": "Foster Parent"
    },
    {
        "value": "CR",
        "label": "Carer"
    },
    {
        "value": "AG",
        "label": "Agent"
    },
    {
        "value": "UN",
        "label": "Unknown"
    }
];
const startingYears: SelectOption[] = [
]

const currentYear = new Date().getFullYear();
for (let i = 0; i < 15; i++) {
    startingYears.push({
        value: `${currentYear + i}`,
        label: `${currentYear + i}`
    });
}


export const EnquiryForm = ({
    isOpen,
    onClose,
    school,
    hasBeenSubmitted,
    setHasBeenSubmitted,
    defaultFormData
}: EnquiryFormProps) => {

    const captchaRef = useRef<ReCAPTCHA>(null)
    const [formError, setFormError] = useState('');


    const handleClose = () => {
        onClose();
        setHasBeenSubmitted(false);
        setFormError('');
    };

    const handleApply = () => {

        let hasErrors = false;

        // Check for required fields
        for (const group of Object.keys(formData)) {
            for (const field of Object.keys(formData[group])) {
                if (formData[group][field].required && !formData[group][field].value) {
                    formData[group][field].errorMessage = 'This field is required';
                    hasErrors = true;
                }
                if (formData[group][field].isValid && !formData[group][field].isValid(formData[group][field].value)) {
                    formData[group][field].errorMessage = formData[group][field].invalidMessage;
                    hasErrors = true;
                }
            }
        }

        // Check that at least on of the interestedIn fields is selected
        if (!formData.interestedIn.generalEnquiry.value && !formData.interestedIn.infoPack.value && !formData.interestedIn.schoolTour.value) {
            formData.interestedIn.schoolTour.errorMessage = 'Please select at least one option';
            hasErrors = true;
        }

        if (hasErrors) {
            setFormData({ ...formData });
            return;
        }

        // Remove all error messages
        for (const group of Object.keys(formData)) {
            for (const field of Object.keys(formData[group])) {
                formData[group][field].errorMessage = '';
            }
        }
        setFormData({ ...formData });


        const consolidatedFormData: { [key: string]: any } = {};
        for (const group of Object.keys(formData)) {
            for (const field of Object.keys(formData[group])) {
                consolidatedFormData[group + field] = formData[group][field].value;
            }
        }


        // Map to the API format
        const apiNamingMap: Record<string, string> = {
            "interestedIngeneralEnquiry": "",
            "interestedIninfoPack": "",
            "interestedInschoolTour": "",
            "primaryContactfirstName": "FirstName",
            "primaryContactlastName": "LastName",
            "primaryContactrelationship": "RelationshipToChildCode",
            "primaryContactemail": "Email",
            "studentDetailsfirstName": "StudentFirstName",
            "studentDetailslastName": "StudentLastName",
            "studentDetailsstartingYear": "StartingYear",
            "studentDetailsadditionalInfo": "AdditionalInfo",
        }


        const apiFormData: { [key: string]: any } = {};
        for (const key of Object.keys(consolidatedFormData)) {
            apiFormData[apiNamingMap[key]] = consolidatedFormData[key];
        }
        // Delete the interestIn fields
        delete apiFormData.interestedIngeneralEnquiry;
        delete apiFormData.interestedIninfoPack;
        delete apiFormData.interestedInschoolTour;
        delete apiFormData[""];


        let formSearchParams = new URLSearchParams(apiFormData).toString();

        // Add the interestIn fields back in
        if (consolidatedFormData.interestedIngeneralEnquiry) {
            formSearchParams += '&InterestedIn=GeneralEnquiry';
        }
        if (consolidatedFormData.interestedIninfoPack) {
            formSearchParams += '&InterestedIn=InfoPack';
        }
        if (consolidatedFormData.interestedInschoolTour) {
            formSearchParams += '&InterestedIn=SchoolTour';
        }

        // Send the school enquiry tracker ID if there is one, otherwise send the school ENO
        formSearchParams += `&SchoolId=${school?.enquiryTrackerId || school?.eno}`;


        if (captchaRef.current) {
            try {

                captchaRef.current.executeAsync?.()
                    .then((token) => {
                        formSearchParams += `&Recaptcha=${token}`;
                        captchaRef?.current?.reset();


                        const requestOptions = {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                            body: formSearchParams
                        };
                        fetcher(`${process.env.REACT_APP_API_URL}/CEMAPI/WebApi/SubmitEnquiryForm`, requestOptions)
                            .then(data => {
                                if (!data.created) {
                                    setFormError('There was an error submitting the form. Please try again later.');
                                    setHasBeenSubmitted(false);
                                    return
                                }
                                setHasBeenSubmitted(true);
                                defaultFormData.interestedIn = JSON.parse(JSON.stringify(formData.interestedIn));
                                defaultFormData.primaryContact = JSON.parse(JSON.stringify(formData.primaryContact));
                                defaultFormData.studentDetails = JSON.parse(JSON.stringify(formData.studentDetails));
                            });
                    })
                    .catch((err) => {
                        console.error('Error submitting form:', err);
                        setFormError('There was an error submitting the form. Please try again later.');
                        setHasBeenSubmitted(false);
                    });

            }
            catch (err) {
                console.error('Error submitting form:', err);
                setFormError('There was an error submitting the form. Please try again later.');
                setHasBeenSubmitted(false);
            }

        }
        else {
            console.error('No token found');
            return;
        }


    };



    const [formData, setFormData] = useState<FormDataType>(defaultFormData);

    return isOpen ? (

        <div className={`absolute top-0 bottom-0 left-0 right-0 max-h-lvh mt-12 pb-12 md:pb-0 md:mb-0 rounded-3xl z-20 bg-white px-5 md:px-8 pt-8 box-content`}>
            <Button
                circle
                colour='golden'
                className='absolute mt-neg-15 mt- right-4'
                onClick={handleClose}>
                <Close variant='dark' />
            </Button>



            {/* Start of the form */}



            <form onSubmit={e => e.preventDefault()} noValidate id='enquiryForm' className='flex flex-col gap-8 pr-2 max-md:pb-20 max-h-full'>
                <h2 className={`text-bluestone text-2xl font-normal`}>Enquiry Form</h2>
                <h2 className={`text-bluestone text-base md:text-2xl font-bold leading-loose text-wrap`}>
                    {school?.school || 'School Name'}
                    <div className={`text-bluestone text-base md:text-2xl font-normal leading-loose`}>{`(${school?.gender || "Gender"}, ${school?.school_level || "Level"})`}</div>
                </h2>

                {hasBeenSubmitted && !formError &&
                    <div className="pb-8 flex-col justify-center items-center gap-4 inline-flex w-full">
                        <div className="w-16 h-16 relative">
                            <CheckCircle variant='light' />
                        </div>
                        <div className="self-stretch text-center text-[#0b223e] text-base font-bold leading-normal">Thanks for your enquiry, we'll be in touch shortly.</div>
                    </div>
                }

                {formError &&
                    <div className='flex flex-col gap-8 pr-2 max-md:pb-20 max-h-full'>
                        <h2 className={`text-bluestone text-base md:text-2xl font-normal`}>Error</h2>
                        <p className='text-slate-500 text-base font-normal'>{formError}</p>
                    </div>
                }


                {!hasBeenSubmitted && !formError &&
                    <div className='overflow-y-scroll flex flex-col gap-8 pr-2 max-md:pb-20 max-h-full'>

                        <div>
                            <strong className={`text-bluestone text-base font-bold`}>I'm interested in </strong>
                            <em className={`text-slate-500 text-base font-normal not-italic`}>(Please select all that apply)</em>
                        </div>

                        <div className='flex flex-col gap-4'>
                            <Control
                                groupName='interestedIn'
                                option='Making a general enquiry'
                                value={formData.interestedIn.generalEnquiry.value}
                                type='checkbox'
                                disabled={false}
                                errorMessage={formData.interestedIn.generalEnquiry.errorMessage}
                                onClick={() => {
                                    formData.interestedIn.generalEnquiry.value = !formData.interestedIn.generalEnquiry.value;
                                    setFormData({ ...formData });
                                }}
                            />
                            <Control
                                groupName='interestedIn'
                                option='Requesting an info pack'
                                value={formData.interestedIn.infoPack.value}
                                type='checkbox'
                                disabled={false}
                                errorMessage={formData.interestedIn.infoPack.errorMessage}
                                onClick={() => {
                                    formData.interestedIn.infoPack.
                                        value = !formData.interestedIn.infoPack.value;
                                    setFormData({ ...formData });
                                }}
                            />
                            <Control
                                groupName='interestedIn'
                                option='A school tour'
                                value={formData.interestedIn.schoolTour.value}
                                type='checkbox'
                                disabled={false}
                                errorMessage={formData.interestedIn.schoolTour.errorMessage}
                                onClick={() => {
                                    formData.interestedIn.schoolTour.value = !formData.interestedIn.schoolTour.value;
                                    setFormData({ ...formData });
                                }}
                            />
                        </div>

                        <div className='flex flex-col gap-5'>
                            <strong className={`text-bluestone text-base font-bold`}>Primary contact details</strong>
                            <Input
                                name="contactFirst"
                                placeholder='First Name'
                                required={formData.primaryContact.firstName.required}
                                value={formData.primaryContact.firstName.value}
                                onChange={(e) => formData.primaryContact.firstName.value = e.target.value}
                                errorMessage={formData.primaryContact.firstName.errorMessage}
                            />
                            <Input
                                name="contactLast"
                                placeholder='Last Name'
                                required={formData.primaryContact.lastName.required}
                                value={formData.primaryContact.lastName.value}
                                onChange={(e) => formData.primaryContact.lastName.value = e.target.value}
                                errorMessage={formData.primaryContact.lastName.errorMessage}
                            />
                            <Select
                                name="contactRelationship"
                                placeholder='Relationship' required=
                                {formData.primaryContact.relationship.required}
                                options={relationships}
                                value={formData.primaryContact.relationship.value}
                                onChange={(e) => formData.primaryContact.relationship.value = e.target.value}
                                errorMessage={formData.primaryContact.relationship.errorMessage}
                            />
                            <Input
                                name="contactEmail"
                                placeholder='Email' required=
                                {formData.primaryContact.email.required}
                                value={formData.primaryContact.email.value}
                                onChange={(e) => formData.primaryContact.email.value = e.target.value}
                                errorMessage={formData.primaryContact.email.errorMessage}
                            />
                        </div>

                        <div className='flex flex-col gap-5'>
                            <strong className={`text-bluestone text-base font-bold`}>Student details</strong>
                            <Input
                                name="studentFirst"
                                placeholder='First Name'
                                required={formData.studentDetails.firstName.required}
                                value={formData.studentDetails.firstName.value}
                                onChange={(e) => formData.studentDetails.firstName.value = e.target.value}
                                errorMessage={formData.studentDetails.firstName.errorMessage}
                            />
                            <Input
                                name="studentLast"
                                placeholder='Last Name'
                                required={formData.studentDetails.lastName.required}
                                value={formData.studentDetails.lastName.value}
                                onChange={(e) => formData.studentDetails.lastName.value = e.target.value}
                                errorMessage={formData.studentDetails.lastName.errorMessage}
                            />
                            <Select
                                name="studentStartYear"
                                placeholder='Starting Year'
                                options={startingYears}
                                required={formData.studentDetails.startingYear.required}
                                value={formData.studentDetails.startingYear.value}
                                onChange={(e) => formData.studentDetails.startingYear.value = e.target.value}
                                errorMessage={formData.studentDetails.startingYear.errorMessage}
                            />
                            <TextArea
                                name="studentAdditionalInfo"
                                placeholder='Please add any additional information or questions here:'
                                required={formData.studentDetails.additionalInfo.required}
                                value={formData.studentDetails.additionalInfo.value}
                                onChange={(e) => formData.studentDetails.additionalInfo.value = e.target.value}
                                errorMessage={formData.studentDetails.additionalInfo.errorMessage}
                            />
                        </div>


                        <div className='flex gap-4 px-5 py-6 max-md:absolute max-md:self-center max-md:bottom-0 max-md:w-full'>
                            {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
                                <ReCAPTCHA
                                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                    ref={captchaRef}
                                    size="invisible"
                                    className='hidden'
                                />
                            )}

                            <Button
                                colour='golden'
                                label='Submit Enquiry'
                                className='w-full'
                                onClick={handleApply}
                            />
                        </div>
                    </div>
                }
            </form>
        </div>

    ) : null;
};
